import React, {useEffect, useMemo, useRef, useState} from 'react';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {ISavedSearchTreeViewResponse} from '../../../api/types/savedSearch.type';
import {
  useRenameNode,
  useSavedSearchTreeView,
} from '../../../hooks/saved-search/savedSearch';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../common/arr-button/ArrButton';
import {useGetMySearchTreeDetails} from '../../../hooks/search/useSearch';
import {
  useDeleteSavedSearch,
  useGetShareGroups,
  useMoveSavedSearches,
  useShareSavedSearch,
  useBatchUploadSavedSearch,
  useDownloadTemplate,
} from '../../../hooks/saved-search/useSavedSearch';
import {SearchesTreeData} from '../../../api/types/searchType.type';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import {t} from 'i18next';
import ArrModal from '../../common/arr-modal/ArrModal';
import {ReactComponent as DownloadIcon} from '../../../assets/images/download_icon.svg';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import './SavedSearchTree.scss';
import {
  expectedHeaders,
  validateHeaders,
  savedSearchMoreMenu,
  getExpandedIdsForNode,
} from '../SavedSearchUtils';
import ExcelJS from 'exceljs';
import {getUUID} from '../../../api/endpoints/authenticationApi';
import {downloadTemplate} from '../../../api/endpoints/savedSearchApi';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {setSelectedTreeItemId} from '../../../redux/slice/savedSearchSlice';

interface ISavedSearchTree {
  getSelectedTreeItem: (item: string) => void;
  getSelectedTreeItemData?: (
    item: TreeViewBaseItem<ExtendedTreeItemProps> | undefined
  ) => void;
}

const SavedSearchTree: React.FC<ISavedSearchTree> = ({
  getSelectedTreeItem,
  getSelectedTreeItemData,
}) => {
  const dispatch = useDispatch();

  const storedSelectedItem = useSelector(
    (state: RootState) => state.savedSearch.selectedTreeItemId
  );
  const {data: treeData, refetch} = useSavedSearchTreeView();
  const {mutate: updateRenameNode} = useRenameNode();
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [openMoveDrawer, setOpenMoveDrawer] = useState(false);
  const [selectedDrawerItem, setSelectedDrawerItem] = useState<string>('');
  const [openShareModal, setOpenShareModal] = useState(false);
  const [shareWith, setShareWith] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [treeKey, setTreeKey] = useState<number>(0);

  const {data: mySearchTreeDetailData} =
    useGetMySearchTreeDetails(openMoveDrawer);
  const {mutate: moveSavedSearches} = useMoveSavedSearches();
  const {mutate: deleteSearch} = useDeleteSavedSearch();
  const {data: sharedSecurityGroups} = useGetShareGroups(openShareModal);
  const {mutate: shareSearch} = useShareSavedSearch();

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const [selectedTreeItem, setSelectedTreeItem] = useState<
    ISavedSearchTreeViewResponse | undefined
  >(undefined);
  const [showBatchUploadModal, setShowBatchUploadModal] =
    useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const [xlFile, setXLFile] = useState<File | string>('');
  const [isError, setIsError] = useState(false);

  const {data: downloadTemplateData, mutate: downloadTemplateMutate} =
    useDownloadTemplate();
  const {
    mutate: batchUploadSavedSearchMutate,
    data: batchUploadResponse,
    error: batchUploadError,
  } = useBatchUploadSavedSearch();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const hasRestoredTreeState = useRef(false);

  const transformToTreeItems = (
    flatList: ISavedSearchTreeViewResponse[] | undefined
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (!Array.isArray(flatList)) return [];

    const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
    const tree: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

    // First pass: create all nodes and store in map
    for (const node of flatList) {
      nodeMap.set(node.id, {
        label: node.textWithoutCount || node.text,
        showMoreOptions: true,
        children: [],
        isDefault: false,
        tooltipTitle: node.toolTipRef_Title,
        tooltipContent: node.toolTipRef_Content,
        ...node,
        isGroup: false,
        id: node.id,
      });
    }

    // Second pass: build tree and set flags
    for (const node of flatList) {
      const current = nodeMap.get(node.id);
      if (!current) continue;

      if (node.parent && node.parent !== '#' && nodeMap.has(node.parent)) {
        const parent = nodeMap.get(node.parent)!;
        (parent.children ??= []).push(current);
        parent.isGroup = true;
      } else if (!node.parent || node.parent === '#') {
        // Treat "#" or undefined/null as root
        tree.push(current);
      }
      // Else: skip orphaned children whose parent is missing
    }

    return tree;
  };

  const transformMySearchTreeDetailsNode = (
    flatList: SearchesTreeData[] | undefined
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (!Array.isArray(flatList)) return [];

    const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
    const tree: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

    // First pass: create all nodes and store in map
    flatList
      .filter((node) => node.text !== 'My Saved Search')
      .forEach((node) => {
        nodeMap.set(node.id, {
          label: node.text,
          showMoreOptions: true,
          children: [],
          isDefault: false,
          isGroup: 'false',
          tooltipTitle: node.toolTipRef_Title || '',
          tooltipContent: node.toolTipRef_Content || '',
          ...node,
        });
      });

    // Second pass: build tree and set flags
    flatList.forEach((node) => {
      const current = nodeMap.get(node.id);
      if (current)
        if (node.parent && nodeMap.has(node.parent)) {
          const parent = nodeMap.get(node.parent)!;
          parent.children && parent.children.push(current);
          parent.isGroup = 'true';
        } else {
          tree.push(current);
          current.isDefault = true;
        }
    });

    return tree;
  };

  const transformedTree = transformToTreeItems(treeData);

  const transformedSearchTreeDetails = transformMySearchTreeDetailsNode(
    mySearchTreeDetailData?.searchesTreeData
  );

  useEffect(() => {
    if (!treeData || !Array.isArray(treeData)) return;

    hasRestoredTreeState.current = false;

    // If Redux has stored value
    if (storedSelectedItem) {
      const storedNode = treeData.find(
        (node) => node.id === storedSelectedItem
      );

      if (storedNode) {
        setSelectedItem(storedNode.id);
        setSelectedTreeItem(storedNode);
        getSelectedTreeItem(storedNode.id);
        getSelectedTreeItemData?.(
          storedNode as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
        );
        return;
      }
    }

    // fallback to first root node
    const firstNode = treeData.find(
      (node) => node.parent === '#' || node.parent === '-1'
    );

    if (firstNode) {
      setSelectedItem(firstNode.id);
      setSelectedTreeItem(firstNode);
      getSelectedTreeItem(firstNode.id);
      getSelectedTreeItemData?.(
        firstNode as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
      );
    }
  }, [treeData]);

  useEffect(() => {
    if (downloadTemplateData) {
      handleDownload();
    }
  }, [downloadTemplateData]);

  useEffect(() => {
    if (batchUploadError) {
      if (batchUploadError.status == 500) {
        triggerToast(
          t('savedSearch.batchUpload.errorUniqueName'),
          ToastType.Error
        );
      } else {
        triggerToast(batchUploadError.message, ToastType.Error);
      }
    } else if (batchUploadResponse) {
      triggerToast(
        t('savedSearch.batchUpload.successMessage'),
        ToastType.Success
      );
      setShowBatchUploadModal(false);
      setFileName('');
      setTimeout(() => {
        refetch();
      }, 3000);
    }
  }, [batchUploadResponse, batchUploadError]);

  useEffect(() => {
    if (
      !hasRestoredTreeState.current &&
      storedSelectedItem &&
      transformedTree.length > 0
    ) {
      const expanded = getExpandedIdsForNode(
        transformedTree,
        storedSelectedItem
      );
      setExpandedIds(expanded);
      setTreeKey((prev) => prev + 1);
      hasRestoredTreeState.current = true;
    }
  }, [storedSelectedItem, JSON.stringify(transformedTree)]);

  const handleSelected = (item: string[] | null) => {
    if (Array.isArray(item) && item.length > 0 && treeData) {
      setSelectedItem(item[0]);
      dispatch(setSelectedTreeItemId(item[0]));
      getSelectedTreeItem(item[0]);
      const foundItem = treeData.find((data: any) => data.id == item[0]);
      setSelectedTreeItem(foundItem);
      getSelectedTreeItemData &&
        getSelectedTreeItemData?.(
          foundItem as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
        );
    } else {
      setSelectedItem('');
      dispatch(setSelectedTreeItemId(null));
      getSelectedTreeItem('');
      setSelectedTreeItem(undefined);
    }
  };

  const onRenameNode = async (
    text: string,
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    const payload = {
      searchGroupLabel: text !== '' ? text : node.label,
      searchGroupID: Number(node.id),
      parentSearchGroupID: node.parent ? Number(node.parent) : 0,
      savedSearchId: 0,
      searchGroupType: '',
      searchGroupSecurityGroupId: node.securitygroupsearchid,
      toolTipRef: '',
    };
    updateRenameNode(payload, {
      onSuccess: (data) => {
        setToastMessage(data.message);
        setToastStatus(ToastType.Success);
        setShowToast(true);
        refetch();
      },
      onError: (error) => {
        setToastMessage(error.message);
        setToastStatus(ToastType.Error);
        setShowToast(true);
      },
    });
  };

  const handleMenuItemClick = (item: {id: string}) => {
    if (item.id === 'move') setOpenMoveDrawer(true);
    if (item.id === 'share') setOpenShareModal(true);
    if (item.id === 'delete') setOpenDeleteModal(true);
    if (item.id === 'batch_upload') setShowBatchUploadModal(true);
    if (item.id === 'add_group_level_error') {
      triggerToast(t('savedSearch.maxLevelError'), ToastType.Error);
    }
  };

  const handleDrawerSelection = (item: string[] | null) => {
    item ? setSelectedDrawerItem(item[0]) : null;
  };

  const onClickDrawerSaveButton = () => {
    const item = mySearchTreeDetailData?.searchesTreeData.find(
      (data) => data.id == selectedItem
    );
    const movePayload = {
      searchGroupID: parseInt(selectedItem),
      parentSearchGroupID: parseInt(selectedDrawerItem),
      searchGroupLabel: item?.text ?? '',
      savedSearchId: 0,
      searchGroupType: 'SGSHARED',
      toolTipRef: item?.toolTipRef_Title ?? 'toolTipRefTitle',
      searchGroupSecurityGroupId: 0,
    };

    moveSavedSearches(movePayload, {
      onSuccess: (result) => {
        triggerToast(result.message, ToastType.Success);
        setOpenMoveDrawer(false);
        refetch();
        setSelectedDrawerItem('');
      },
      onError: () => {
        triggerToast(
          t('savedSearch.savedSearchTreeFlyout.moveError'),
          ToastType.Error
        );
      },
    });
  };

  const handleShare = () => {
    const sharedGroup = sharedSecurityGroups?.securityGroups.find(
      (item) => item.toGroupID.toString() == shareWith
    );

    const item = treeData?.find((data) => data.id == selectedItem);

    if (!sharedGroup || !selectedItem) {
      return;
    }
    const sharePayload = {
      searchID: parseInt(selectedItem),
      toGroupID: sharedGroup.toGroupID,
      isSearchGroup: true,
      destSecurityGroupID: sharedGroup.securityGroupID,
      notificationURL: sharedGroup?.notificationURL ?? '',
      emailId: sharedGroup?.emailId ?? '',
      name: item?.text ?? 'string',
      shareType: 'SGSHARED',
      originalSearchID: sharedGroup.originalSearchID,
    };
    shareSearch(sharePayload, {
      onSuccess: (result) => {
        triggerToast(result.successMessage, ToastType.Success);
        setOpenShareModal(false);
        setTimeout(() => refetch(), 10);
        setShareWith('');
      },
      onError: () => {
        triggerToast(t('savedSearch.shareModal.shareError'), ToastType.Error);
      },
    });
  };

  const handleConfirmDelete = () => {
    const node = treeData?.find((item) => item.id == selectedItem);
    if (!node || !selectedItem) {
      return;
    }
    const deletePayload = {
      searchGroupID: Number(selectedItem),
      parentSearchGroupID: parseInt(selectedItem),
      searchGroupLabel: node.text,
      roleID: loginUserData.roleId,
      userID: loginUserData.userId,
      securityGroupID: node.saveSearchSecurityGroupId,
      projectId: loginUserData.projectId,
      savedSearchId: 0,
      searchGroupType: node?.parent,
      searchGroupSecurityGroupId: node.saveSearchSecurityGroupId,
      toolTipRef: 'a',
    };
    deleteSearch(deletePayload, {
      onSuccess: (result) => {
        triggerToast(result.successMessage, ToastType.Success);
        setOpenDeleteModal(false);
        setSelectedItem('');
        setSelectedTreeItem(undefined);
        refetch();
      },
      onError: () => {
        triggerToast(t('savedSearch.deleteModal.deleteError'), ToastType.Error);
      },
    });
  };

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const handleFileValidation = (acceptedFiles: File[]) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const file = acceptedFiles[0];

    if (file) {
      if (allowedTypes.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const binaryStr = e.target?.result;
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(binaryStr);
            const worksheet = workbook.worksheets[0];
            const headersJson = [];
            worksheet.eachRow((row) => {
              headersJson.push(row.values.slice(1));
            });
            const headersRow = headersJson[0] || [];
            const filteredheader = headersRow.filter(
              (item: string | number) => item !== 0
            );
            if (validateHeaders(filteredheader, expectedHeaders)) {
              setFileName(file.name);
              setXLFile(file);
            } else {
              setIsError(true);
              triggerToast(
                t('savedSearch.batchUpload.errorValidFormat'),
                ToastType.Error
              );
            }
          } catch (error) {
            console.log(error);
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        triggerToast(
          t('savedSearch.batchUpload.errorValidExtension'),
          ToastType.Error
        );
      }
    }
  };
  const handleDownload = async () => {
    try {
      const blob = await downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'BatchSearchTemplate.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading the template:', error);
    }
  };

  const isGroup = useMemo(
    () => treeData?.find((item) => item.parent === selectedItem),
    [selectedItem]
  );

  const moreMenuOptions = useMemo(
    () =>
      savedSearchMoreMenu(
        loginUserData.roleId,
        selectedTreeItem?.parent,
        true,
        isGroup != undefined ? true : false
      ),
    [selectedItem]
  );

  return (
    <Box
      className="saved-search-tree-container"
      data-testid="saved-saearch-tree-container"
    >
      <ArrTreeView
        key={treeKey}
        setSelectedItem={handleSelected}
        showTooltip
        itemsList={transformedTree}
        selectedItem={selectedItem}
        handleNoResultMessage={'Loading...'}
        allowGlobalSelect={false}
        treeSelectedItems={[selectedItem]}
        multipleSelection={false}
        isShowCountLabel={true}
        showActions={true}
        onRenameNode={onRenameNode}
        MoreMenuOptions={moreMenuOptions}
        onMenuItemClick={handleMenuItemClick}
        menuDirection="bottom"
        menuClassName={`saved-tree-menu  ${
          selectedTreeItem?.parent !== '#'
            ? moreMenuOptions.find(
                (menuItem) => menuItem.id === 'batch_upload'
              ) !== undefined
              ? 'with-border-second-child'
              : 'with-border-first-child'
            : ''
        }`}
        expandSelect={false}
        defaultExpandedIds={expandedIds}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
      {openMoveDrawer && (
        <ArrDrawer
          transition={300}
          onClose={() => setOpenMoveDrawer(false)}
          title={t('savedSearch.moveDrawer.title')}
          side="right"
          size="large"
          showButton={false}
          showHeader={false}
          className="search-group-move-drawer"
          isOpen={openMoveDrawer}
        >
          <Box className="search-group-move-drawer__header">
            <Box className="title-box">
              <Typography
                className=" typo-text headline3_size title"
                data-testid={`saved-search-tree-flyout-title`}
              >
                {t('savedSearch.savedSearchTreeFlyout.flyoutTitle')}
              </Typography>
            </Box>
            <Box className="action-buttons-box">
              <ArrButton
                variant="secondary"
                size="medium"
                onClick={() => {
                  setOpenMoveDrawer(false);
                }}
                disabled={false}
                aria-label={t('common.cancel')}
                dataTestId="saved-search-tree-cancel-button"
              >
                {t('common.cancel')}
              </ArrButton>
              <ArrButton
                disabled={!selectedDrawerItem}
                variant="primary"
                size="medium"
                type="submit"
                aria-label={t('common.save')}
                dataTestId="saved-search-tree-save-button"
                onClick={onClickDrawerSaveButton}
              >
                {t('common.save')}
              </ArrButton>
            </Box>
          </Box>
          <Box className="search-group-move-drawer__content">
            <ArrTreeView
              setSelectedItem={handleDrawerSelection}
              itemsList={transformedSearchTreeDetails}
              selectedItem={selectedDrawerItem}
              handleNoResultMessage={'Loading...'}
              allowGlobalSelect={false}
              treeSelectedItems={[selectedDrawerItem]}
              multipleSelection={false}
              isShowCountLabel={true}
              isShowCheckbox={false}
            />
          </Box>
        </ArrDrawer>
      )}
      <ArrModal
        showModal={openShareModal}
        onClose={() => {
          setShareWith('');
          setOpenShareModal(false);
        }}
        className="saved-search-share-modal"
        title={t('savedSearch.shareModal.title')}
        description={t('savedSearch.shareModal.description')}
        ariaLabel={t('savedSearch.shareModal.title')}
        ariaDescription={t('savedSearch.shareModal.description')}
        dataTestId="saved-search-share-modal"
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => {
              setShareWith('');
              setOpenShareModal(false);
            },
            variant: 'secondary',
            dataTestId: 'sst-cancel-btn',
          },
          {
            id: '2',
            label: t('common.save'),
            onClick: () => handleShare(),
            variant: 'primary',
            dataTestId: 'sst-save-btn',
            disabled: !shareWith,
          },
        ]}
      >
        <Box className="modal-container">
          <ul data-testid={`saved-search-tree-ul`}>
            <li
              className="typo-text body2_size error-text"
              data-testid={`saved-search-tree-shared-with-li`}
            >
              <ArrRadio
                dataTestId="selectItem"
                value={shareWith}
                name={'selectItem'}
                data={
                  sharedSecurityGroups
                    ? sharedSecurityGroups.securityGroups.map((opt) => {
                        return {
                          label: opt.name,
                          value: opt.toGroupID.toString(),
                        };
                      })
                    : []
                }
                onChange={(e) => {
                  setShareWith(e);
                }}
              />
            </li>
          </ul>
        </Box>
      </ArrModal>
      <ArrModal
        showModal={openDeleteModal}
        title={t('savedSearch.deleteGroupModal.title')}
        description={t('savedSearch.deleteGroupModal.description')}
        ariaLabel={t('savedSearch.deleteGroupModal.title')}
        ariaDescription={t('savedSearch.deleteGroupModal.description')}
        onClose={() => setOpenDeleteModal(false)}
        dataTestId="savedsearcg-deletegroup-modal"
        buttons={[
          {
            id: 'cancel-delete',
            label: t('common.cancel'),
            onClick: () => setOpenDeleteModal(false),
            variant: 'secondary',
            dataTestId: 'ssd-cancel-del-btn',
          },
          {
            id: 'confirm-delete',
            label: t('common.ok'),
            onClick: () => {
              handleConfirmDelete();
            },
            variant: 'primary',
            dataTestId: 'ssd-confirm-del-btn',
          },
        ]}
        className="Delete-security-group-modal"
      />
      <ArrModal
        showModal={showBatchUploadModal}
        onClose={() => {
          setFileName('');
          setShowBatchUploadModal(false);
        }}
        className="saved-search-tree-modal"
        title={t('savedSearch.batchUpload.title')}
        ariaLabel={t('savedSearch.batchUpload.title')}
        dataTestId="savedsearch-batchupload-modal"
        titleEndIcon={
          <ArrTooltipWrapper
            title={t('savedSearch.batchUpload.title')}
            trigger={
              <InfoIcon
                fontSize="medium"
                className="saved-search-tree-info-icon label1_size"
                data-testid="savedSearch-batchUpload-info"
              />
            }
          >
            {t('savedSearch.batchUpload.titleInfo')}
          </ArrTooltipWrapper>
        }
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => {
              setFileName('');
              setShowBatchUploadModal(false);
            },
            variant: 'secondary',
            dataTestId: 'ssb-cancel-btn',
          },
          {
            id: '2',
            label: t('common.save'),
            onClick: () => {
              const formData = new FormData();
              formData.append('BatchSearchFile', xlFile);
              const params = {
                SearchGroupId: selectedItem,
                SecurityGroupId: selectedTreeItem?.securitygroupsearchid,
                SessionId: getUUID(),
              };
              batchUploadSavedSearchMutate({formData, params});
            },
            variant: 'primary',
            dataTestId: 'ssb-save-btn',
            disabled: !fileName || isError,
          },
        ]}
      >
        <Box className="saved-search-tree-selection-container">
          <label className="label-text" data-testid={`saved-search-tree-label`}>
            <span
              className="typo-text body2_size"
              data-testid={`saved-search-file-name`}
            >
              {fileName || t('savedSearch.batchUpload.chooseFile')}
            </span>
          </label>

          <input
            ref={fileInputRef}
            style={{display: 'none'}}
            data-testid="file-input"
            type="file"
            onChange={(e) => {
              if (e.target.files) {
                e.target.blur();
                handleFileValidation(Array.from(e.target.files));
              }
            }}
            accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          />
          <ArrButton
            variant="primary"
            size="medium"
            type="submit"
            aria-label={t('common.select')}
            dataTestId="saved-search-tree-select-button"
            onClick={() => {
              fileInputRef.current?.click();
            }}
          >
            {t('common.select')}
          </ArrButton>
        </Box>
        <ArrButton
          variant="text"
          size="medium"
          type="submit"
          aria-label={t('savedSearch.batchUpload.downloadTemplate')}
          dataTestId="saved-search-tree-download-button"
          startIcon={<DownloadIcon fontSize="medium" />}
          onClick={() => {
            downloadTemplateMutate({});
          }}
        >
          {t('savedSearch.batchUpload.downloadTemplate')}
        </ArrButton>
      </ArrModal>
    </Box>
  );
};

export default SavedSearchTree;
